import { computePL, periodRange } from "./biz.js";
import { wageBase, taxPeriod } from "./taxData.js";

// Schedule C expense lines (Part II). Line numbers have been stable for years but check them against the current form.
const LINES = [
  { id: "advertising", line: "8", label: "Advertising", re: /advertis|marketing|promotion/i },
  { id: "car", line: "9", label: "Car and truck expenses", re: /\b(car|vehicle|fuel|gas|auto)\b|mileage/i },
  { id: "fees", line: "10", label: "Commissions and fees", re: /\bfees?\b|commission|bank charge/i },
  { id: "contract", line: "11", label: "Contract labor", re: /contract|freelanc/i },
  { id: "insurance", line: "15", label: "Insurance (other than health)", re: /insur/i },
  { id: "legal", line: "17", label: "Legal and professional services", re: /legal|attorney|accountant|professional|consult|bookkeep/i },
  { id: "office", line: "18", label: "Office expense", re: /office|postage|stationery/i },
  { id: "rent", line: "20", label: "Rent or lease", re: /\brent\b|lease|coworking/i },
  { id: "supplies", line: "22", label: "Supplies", re: /suppl/i },
  { id: "travel", line: "24a", label: "Travel", re: /travel|airfare|hotel|lodging/i },
  { id: "meals", line: "24b", label: "Meals (50% deductible)", re: /meal|dining|restaurant|entertain/i, factor: 0.5 },
  { id: "utilities", line: "25", label: "Utilities", re: /utilit|phone|internet/i },
  { id: "other", line: "27a", label: "Other expenses (incl. software)", re: /./ },
];
export function scheduleCLine(category) {
  return LINES.find((l) => l.re.test(category || "")) || LINES[LINES.length - 1];
}

// Self-employment tax: 15.3% of 92.35% of net earnings; the 12.4% part stops at the wage base.
export function seTax(net, year) {
  if (!(net >= 400)) return { base: 0, socialSecurity: 0, medicare: 0, total: 0, halfDeduction: 0 };
  const base = net * 0.9235;
  const ss = Math.min(base, wageBase(year)) * 0.124;
  const medicare = base * 0.029;
  const total = Math.round((ss + medicare) * 100) / 100;
  return { base, socialSecurity: ss, medicare, total, halfDeduction: total / 2 };
}

// Everything the tax screen shows for one date range.
export function computeTaxSummary(profile, range, { year, incomeTaxRate = 12 }) {
  const pl = computePL(profile, range);
  const byLine = {};
  pl.categoryList.forEach((c) => {
    const l = scheduleCLine(c.name);
    const ded = c.total * (l.factor ?? 1);
    byLine[l.id] = byLine[l.id] || { ...l, spent: 0, deductible: 0, cats: [] };
    byLine[l.id].spent += c.total;
    byLine[l.id].deductible += ded;
    byLine[l.id].cats.push(c.name);
  });
  const add = (id, spent) => {
    if (!spent) return;
    const l = LINES.find((x) => x.id === id);
    byLine[id] = byLine[id] || { ...l, spent: 0, deductible: 0, cats: [] };
    byLine[id].spent += spent; byLine[id].deductible += spent * (l.factor ?? 1);
  };
  add("car", pl.mileage);
  const wagesTotal = (pl.wages || 0) + (pl.unpaidLabor || 0);
  if (wagesTotal) byLine.wages = { id: "wages", line: "26", label: "Wages", spent: wagesTotal, deductible: wagesTotal, cats: [pl.wages ? "Payslips" : "", pl.unpaidLabor ? "Hours not yet on a payslip" : ""].filter(Boolean) };
  if (pl.employerCosts) byLine.payrollTaxes = { id: "payrollTaxes", line: "23", label: "Taxes and licenses (employer payroll costs)", spent: pl.employerCosts, deductible: pl.employerCosts, cats: ["Employer costs on payslips"] };
  const lines = Object.values(byLine).sort((a, b) => parseFloat(a.line) - parseFloat(b.line));
  const deductible = lines.reduce((s, l) => s + l.deductible, 0);
  const net = pl.revenue - deductible;
  return { pl, lines, deductible, notDeductible: lines.reduce((s, l) => s + (l.spent - l.deductible), 0), revenue: pl.revenue, net, year, incomeTaxRate };
}

// Pay-as-you-go estimate through the end of a payment period (tax on profit so far, minus what's been paid).
export function estimateThroughPeriod(profile, year, q, { incomeTaxRate = 12, payments = [] }) {
  const p = taxPeriod(year, q);
  const ytd = computeTaxSummary(profile, { start: `${year}-01-01`, end: p.end }, { year, incomeTaxRate });
  const se = seTax(ytd.net, year);
  const incomeBase = Math.max(0, ytd.net - se.halfDeduction);
  const incomeTax = incomeBase * (incomeTaxRate / 100);
  const taxSoFar = se.total + incomeTax;
  const paid = payments.filter((x) => x.year === year && x.q <= q).reduce((s, x) => s + (Number(x.amount) || 0), 0);
  return { period: p, ytd, se, incomeTax, taxSoFar, paid, suggested: Math.max(0, Math.round((taxSoFar - paid) * 100) / 100) };
}

// "Safe harbor" alternative: generally 100% of last year's total tax (110% if last year's AGI was over $150,000), in four equal parts.
export function safeHarborQuarter(priorYearTax, highIncome) {
  const t = Number(priorYearTax) || 0;
  return Math.round(t * (highIncome ? 1.1 : 1) / 4 * 100) / 100;
}
export { periodRange };


// ---- other countries: same P&L basis, tax rules come from countries.js --------------------------------------
import { runEstimate, thresholdStatus, countrySettings, fractionElapsed, taxYearRange, taxYearOf } from "./countries.js";
const MEALS_RE = /meal|dining|restaurant|entertain/i;

// Net profit for a tax year with the deductions this country context allows: user categories (meals/entertainment at a
// chosen %), mileage (official rate if one exists), wages from payslips and employer costs.
export function computeTaxSummaryIntl(profile, range, { mealsPct = 50 } = {}) {
  const pl = computePL(profile, range);
  const lines = pl.categoryList.map((c) => {
    const factor = MEALS_RE.test(c.name) ? mealsPct / 100 : 1;
    return { id: c.name, label: c.name, spent: c.total, deductible: c.total * factor, partial: factor !== 1 };
  });
  const add = (id, label, amount) => { if (amount) lines.push({ id, label, spent: amount, deductible: amount, partial: false }); };
  add("mileage", "Vehicle mileage", pl.mileage);
  add("wages", "Staff wages", (pl.wages || 0) + (pl.unpaidLabor || 0));
  add("employer", "Employer payroll costs", pl.employerCosts);
  const deductible = lines.reduce((s, l) => s + l.deductible, 0);
  return { pl, lines, deductible, revenue: pl.revenue, net: pl.revenue - deductible };
}

// Everything the country tax screen needs: profit so far, tax on it, a projection, and the registration-threshold tracker.
export function computeIntl(profile, country, startYear, settings = {}, today = localDate()) {
  const range = taxYearRange(country, startYear);
  const mealsPct = Number(settings.mealsPct ?? 50);
  const summary = computeTaxSummaryIntl(profile, range, { mealsPct });
  const frac = fractionElapsed(range, today);
  const so = runEstimate(country, summary.net, { revenue: summary.revenue, settings });
  const projected = frac > 0.08 && frac < 1 ? runEstimate(country, summary.net / frac, { revenue: summary.revenue / frac, settings }) : null;
  const back12 = (() => { const d = new Date(today + "T00:00:00"); d.setFullYear(d.getFullYear() - 1); d.setDate(d.getDate() + 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
  const trailing = computePL(profile, { start: back12, end: today }).revenue;
  return { range, summary, fraction: frac, estimate: so, projected, projectedProfit: frac > 0.08 && frac < 1 ? summary.net / frac : null, threshold: thresholdStatus(country, trailing), trailingRevenue: trailing, settings: countrySettings(country, settings) };
}
function localDate() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }
export { taxYearOf };
